import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonSegment,
  IonSegmentButton,
  IonLabel,
} from '@ionic/react';
import { useQuery } from '@apollo/client/react';
import { GET_PAST_BIDS } from '../../../graphql/queries/pastBids';
import { GET_PAST_TRANSFER_OFFERS } from '../../../graphql/queries/pastTransferOffers';
import type {
  GetPastBidsQuery,
  GetPastBidsQueryVariables,
  GetPastTransferOffersQuery,
  GetPastTransferOffersQueryVariables,
} from '../../../types/graphql-generated';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PastOffersList, type PastOfferItem } from './PastOffersList';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';

interface PastBidsPageProps {
  leagueId: string;
}

const LIMIT = 20;

type Segment = 'market' | 'transferOffers';

export const PastBidsPage = ({ leagueId }: PastBidsPageProps) => {
  const navigate = useNavigate();
  const [activeSegment, setActiveSegment] = useState<Segment>('market');

  // --- Market bids state ---
  const [bidsOffset, setBidsOffset] = useState(0);
  const [bidsHasMore, setBidsHasMore] = useState(true);

  const { data: bidsData, loading: bidsLoading, error: bidsError, fetchMore: fetchMoreBids } = useQuery<
    GetPastBidsQuery,
    GetPastBidsQueryVariables
  >(GET_PAST_BIDS, {
    variables: { fantasyLeagueId: leagueId, limit: LIMIT, offset: 0 },
  });

  // --- Transfer offers state ---
  const [offersOffset, setOffersOffset] = useState(0);
  const [offersHasMore, setOffersHasMore] = useState(true);

  const { data: offersData, loading: offersLoading, error: offersError, fetchMore: fetchMoreOffers } = useQuery<
    GetPastTransferOffersQuery,
    GetPastTransferOffersQueryVariables
  >(GET_PAST_TRANSFER_OFFERS, {
    variables: { fantasyLeagueId: leagueId, limit: LIMIT, offset: 0 },
  });

  const rawBids = bidsData?.fantasyLeagueMemberPastBids || [];
  const rawOffers = offersData?.fantasyLeagueMemberPastTransferOffers || [];

  const bidItems: PastOfferItem[] = rawBids.map((bid) => ({
    id: bid.id,
    player: bid.marketEntry.player,
    amount: bid.amount,
    status: bid.status,
    createdAt: bid.createdAt,
    sellerName: bid.marketEntry.seller?.teamName || 'Futyclub',
    sellerMemberId: bid.marketEntry.seller?.id,
    bidderName: bid.bidder?.teamName || 'Futyclub',
    bidderMemberId: bid.bidder?.id,
  }));

  const offerItems: PastOfferItem[] = rawOffers.map((offer) => ({
    id: offer.id,
    player: offer.player,
    amount: offer.amount,
    status: offer.status,
    createdAt: offer.createdAt,
    sellerName: offer.seller?.teamName || 'Futyclub',
    sellerMemberId: offer.seller?.id,
    bidderName: offer.bidder?.teamName || 'Futyclub',
    bidderMemberId: offer.bidder?.id,
    releaseClause: offer.releaseClause,
  }));

  const handleLoadMoreBids = async (event: CustomEvent<void>) => {
    const newOffset = bidsOffset + LIMIT;
    try {
      const result = await fetchMoreBids({
        variables: { fantasyLeagueId: leagueId, limit: LIMIT, offset: newOffset },
      });
      if ((result.data?.fantasyLeagueMemberPastBids || []).length < LIMIT) {
        setBidsHasMore(false);
      }
      setBidsOffset(newOffset);
    } catch (err) {
      console.error('Error loading more bids:', err);
    } finally {
      (event.target as HTMLIonInfiniteScrollElement).complete();
    }
  };

  const handleLoadMoreOffers = async (event: CustomEvent<void>) => {
    const newOffset = offersOffset + LIMIT;
    try {
      const result = await fetchMoreOffers({
        variables: { fantasyLeagueId: leagueId, limit: LIMIT, offset: newOffset },
      });
      if ((result.data?.fantasyLeagueMemberPastTransferOffers || []).length < LIMIT) {
        setOffersHasMore(false);
      }
      setOffersOffset(newOffset);
    } catch (err) {
      console.error('Error loading more offers:', err);
    } finally {
      (event.target as HTMLIonInfiniteScrollElement).complete();
    }
  };

  const isInitialLoading = activeSegment === 'market'
    ? (bidsLoading && bidsOffset === 0)
    : (offersLoading && offersOffset === 0);

  const activeError = activeSegment === 'market' ? bidsError : offersError;
  const handleMemberClick = (fantasyLeagueMemberId: string) => {
    navigate(`/app/ligas/${leagueId}/mercado/plantilla/${fantasyLeagueMemberId}`);
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/ligas/${leagueId}/mercado`} />
          </IonButtons>
          <IonTitle>Historial de ofertas</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        <IonToolbar>
          <IonSegment
            value={activeSegment}
            onIonChange={(e) => setActiveSegment(e.detail.value as Segment)}
          >
            <IonSegmentButton value="market">
              <IonLabel>De mercado</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="transferOffers">
              <IonLabel>Ofertas directas</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
        <p className="text-sm text-gray-500 px-2 mt-2">
          {activeSegment === 'market'
            ? 'Ofertas realizadas por jugadores que se encontraban en venta en el mercado.'
            : 'Ofertas realizadas directamente a un participante de la liga sin estar el jugador en venta.'}
        </p>
        <div className="mt-4" />
        {isInitialLoading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {activeError && (
          <div className="text-red-600 text-sm px-2">
            <p>Error al cargar las ofertas pasadas</p>
          </div>
        )}

        {!isInitialLoading && activeSegment === 'market' && (
          <PastOffersList
            items={bidItems}
            hasMore={bidsHasMore}
            loading={bidsLoading}
            onLoadMore={handleLoadMoreBids}
            onMemberClick={handleMemberClick}
            emptyMessage="Todavía no hay ofertas por jugadores que estuvieran en venta en el mercado."
          />
        )}

        {!isInitialLoading && activeSegment === 'transferOffers' && (
          <PastOffersList
            items={offerItems}
            hasMore={offersHasMore}
            loading={offersLoading}
            onLoadMore={handleLoadMoreOffers}
            onMemberClick={handleMemberClick}
            emptyMessage="Todavía no hay ofertas directas a participantes de la liga."
          />
        )}
      </IonContent>
    </IonPage>
  );
};
